import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { lastValueFrom } from 'rxjs';
import * as XLSX from 'xlsx';
import moment from 'moment';

import {
  DynamicListingComponent,
  FilterConfig,
  ColumnConfig,
  ActionConfig,
  ActionEvent,
  FilterEvent
} from '../dynamic-listing/dynamic-listing';
import { SVG_ICONS } from '../utils/svg-icons.utils';
import { ClientOrderService } from '../services/client/client-order.service';
import { ClientListService } from '../services/client/client-list.service';
import { ToasterService } from '@ui-nuvertos/auth';

@Component({
  selector: 'lib-client-so-list',
  standalone: true,
  imports: [CommonModule, DynamicListingComponent],
  templateUrl: './client-so-list.html',
  styleUrl: './client-so-list.css',
})
export class ClientSoList implements OnInit {
  exportFilename = `client_sales_orders_${new Date().toISOString().slice(0, 10)}`;

  tableData: any[] = [];
  totalCount = 0;
  totalPages = 0;
  isLoading = false;
  isLoadingClients = false;
  pageIndex = 1;
  rowsPerPage = 10;

  orderNumber: string = '';
  orderDate: Date | null = null;
  receiveByDate: Date | null = null;
  selectedClient: string = '';
  priorityLevel: string = '';

  clientOptions: any[] = [];

  filterConfigs: FilterConfig[] = [];

  statusOptions = [
    { label: 'Submitted', value: 'submitted' },
    { label: 'Approved', value: 'approved' },
  ];
  status: string = '';

  columnConfigs: ColumnConfig[] = [
    { key: 'order_no', header: 'SO #' },
    { key: 'order_date', header: 'Order Date', type: 'datetime' },
    { key: 'client_code', header: 'Client Code' },
    { key: 'client_name', header: 'Client Name' },
    { key: 'gross_amount', header: 'Gross Amount' },
    { key: 'final_amount', header: 'Total Amount' },
    { key: 'memo', header: 'Memo', ellipsis: true },
    { key: 'status', header: 'Status', type: 'status' },
    { key: 'approval_status', header: 'Approval Status' }
  ];

  actionConfigs: ActionConfig[] = [
    {
      key: 'view',
      label: 'View',
      tooltip: 'View',
      severity: 'secondary',
      svgIcon: SVG_ICONS.VIEW
    },
    {
      key: 'edit',
      label: 'Edit',
      tooltip: 'Edit',
      severity: 'secondary',
      svgIcon: SVG_ICONS.EDIT
    },
    {
      key: 'delete',
      label: 'Delete',
      tooltip: 'Delete',
      severity: 'secondary',
      svgIcon: SVG_ICONS.DELETE
    }
  ];

  constructor(
    private router: Router,
    private clientOrderService: ClientOrderService,
    private clientListService: ClientListService,
    private toasterService: ToasterService
  ) {}

  async ngOnInit() {
    this.initializeFilterConfigs();
    await Promise.all([this.loadClients(), this.loadClientOrders()]);
    this.initializeFilterConfigs();
  }

  initializeFilterConfigs() {
    this.filterConfigs = [
      {
        key: 'orderDate',
        type: 'date',
        placeholder: 'Order date',
        value: this.orderDate,
        showInAccordion: true,
        showInDialog: false
      },
      {
        key: 'orderNumber',
        type: 'text',
        placeholder: 'SO number',
        value: this.orderNumber,
        showInAccordion: true,
        showInDialog: false
      },
      {
        key: 'selectedClient',
        type: 'select',
        placeholder: 'Client',
        options: this.clientOptions,
        optionLabel: 'label',
        optionValue: 'value',
        value: this.selectedClient,
        loading: this.isLoadingClients,
        showInAccordion: true,
        showInDialog: false
      },
      // {
      //   key: 'status',
      //   type: 'select',
      //   placeholder: 'Status',
      //   options: this.statusOptions,
      //   optionLabel: 'label',
      //   optionValue: 'value',
      //   value: this.status,
      //   showInAccordion: true,
      //   showInDialog: false
      // }
    ];
  }

  async loadClientOrders(page: number = 1) {
    try {
      this.isLoading = true;
      this.pageIndex = page;

      const queryParams: any = {
        offset: (this.pageIndex - 1) * this.rowsPerPage,
        limit: this.rowsPerPage
      };

      if (this.orderNumber && this.orderNumber.trim()) {
        queryParams['order_no'] = this.orderNumber.trim();
      }

      if (this.selectedClient && this.selectedClient.trim()) {
        queryParams['client_id'] = this.selectedClient.trim();
      }

      if (this.orderDate instanceof Date) {
        queryParams['order_date'] = moment(this.orderDate).format('YYYY-MM-DD');
      }

      if (this.status && this.status.trim()) {
        queryParams.status = this.status.trim();
      }

      const response = await lastValueFrom(this.clientOrderService.getClientOrders(queryParams));

      if (response?.status === 'success') {
        const rows = response?.data?.orders || [];
        this.tableData = rows.map((row: any) => ({
          id: row.id,
          order_no: row.order_no ?? row.id ?? '-',
          order_date: row.order_date ? moment(row.order_date).format('DD/MM/YYYY') : '-',
          client_code: row.client?.client_code ?? '-',
          client_name: row.client?.client_name ?? '-',
          memo: row.memo ?? '-',
          gross_amount: row.gross_amount ?? '-',
          final_amount: row.final_amount ?? '-',
          status: this.formatStatus(row.status || 'open'),
          approval_status: row.approval_status ? String(row.approval_status).replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase()) : '-',
          raw: row
        }));
        this.totalCount = response?.data?.total ?? rows.length ?? 0;
        const calculatedPages = this.totalCount && this.rowsPerPage
          ? Math.ceil(this.totalCount / this.rowsPerPage)
          : 0;
        this.totalPages = calculatedPages > 0 ? calculatedPages : (this.tableData.length > 0 ? 1 : 0);
      } else {
        this.tableData = [];
        this.totalCount = 0;
        this.totalPages = 0;
      }
    } catch (error) {
      console.error('Failed to load client sales orders', error);
      this.tableData = [];
      this.totalCount = 0;
      this.totalPages = 0;
    } finally {
      this.isLoading = false;
    }
  }

  async loadClients() {
    try {
      this.isLoadingClients = true;
      const response = await lastValueFrom(this.clientListService.getClients({ limit: 200, offset: 0 }));
      const list = response?.data?.clients || response?.data || [];
      this.clientOptions = Array.isArray(list)
        ? list.map((client: any) => ({
            label: client.client_name || client.client_code || 'Unknown',
            value: String(client.id ?? client.clientId ?? client.client_id ?? '')
          })).filter(option => option.value)
        : [];
      this.initializeFilterConfigs();
    } catch (error) {
      console.error('Failed to load clients for filter', error);
      this.clientOptions = [];
    } finally {
      this.isLoadingClients = false;
    }
  }

  exportXlsx() {
    const rows = this.tableData.map((row, index) => ({
      'S.No.': (this.pageIndex - 1) * this.rowsPerPage + index + 1,
      'SO #': row.order_no || 'NA',
      'Order Date': row.order_date || 'NA',
      'Client Code': row.client_code || 'NA',
      'Client Name': row.client_name || 'NA',
      'Memo': row.memo || 'NA',
    }));

    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sales Orders');
    XLSX.writeFile(workbook, `${this.exportFilename}.xlsx`);
  }

  exportPdf() {
    window.print();
  }

  exportCsv() {
    const headers = ['S.No.', ...this.columnConfigs.map(column => column.header)];
    const csvRows = [headers.join(',')];

    this.tableData.forEach((row, index) => {
      const values = [String((this.pageIndex - 1) * this.rowsPerPage + index + 1)];
      this.columnConfigs.forEach(column => {
        const value = row[column.key];
        const sanitized = (value ?? '-')
          .toString()
          .replace(/"/g, '""');
        if (sanitized.search(/([",\n])/g) >= 0) {
          values.push(`"${sanitized}"`);
        } else {
          values.push(sanitized);
        }
      });
      csvRows.push(values.join(','));
    });

    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.href = url;
    link.setAttribute('download', `${this.exportFilename}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  view(orderId: string) {
    if (!orderId) return;
    this.router.navigate(['/clients/sales-order'], {
      queryParams: {
        soId: orderId,
        mode: 'view'
      }
    });
  }

  edit(orderId: string) {
    if (!orderId) return;
    this.router.navigate(['/clients/sales-order'], {
      queryParams: {
        soId: orderId,
        mode: 'edit'
      }
    });
  }

  async delete(orderId: string) {
    if (!orderId) return;
    try {
      this.isLoading = true;
      await lastValueFrom(this.clientOrderService.deleteClientOrder(orderId));
      await this.loadClientOrders(this.pageIndex);
    } catch (error) {
      console.error('Failed to delete sales order', error);
      this.isLoading = false;
    }
  }

  onFilterEvent(event: FilterEvent) {
    this.orderNumber = event.data?.orderNumber || '';
    this.orderDate = event.data?.orderDate || null;
    this.selectedClient = event.data?.selectedClient || '';
    this.status = event.data?.status || '';

    this.pageIndex = 1;
    this.loadClientOrders(1);
  }

  onActionClicked(event: ActionEvent) {
    switch (event.action) {
      case 'view':
        this.view(event.row.id);
        break;
      case 'edit':
        // Prevent editing if order is approved
        if (event.row.approval_status === 'approved') {
          this.toasterService.warn('Warning', 'Cannot edit sales order with approved status');
          return;
        }
        // Prevent editing if order status is closed
        const orderStatus = event.row.raw?.status || event.row.status;
        if (orderStatus === 'closed') {
          this.toasterService.warn('Warning', 'Cannot edit sales order with closed status');
          return;
        }
        this.edit(event.row.id);
        break;
      case 'delete':
        this.delete(event.row.id);
        break;
    }
  }

  onPageChanged(page: number) {
    this.loadClientOrders(page);
  }

  async onColumnsSelected(selectedColumns: string[]) {
    // Placeholder for future backend integration with column preferences
    await this.loadClientOrders(this.pageIndex);
  }

  formatStatus(status: string): string {
    if (!status) return 'Open';
    // Convert snake_case to Title Case
    return status
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }
}